import { motion } from 'framer-motion';
import { Plus, Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { h5, sh2 } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import Card from '../../ui/card';
import IconTextButton from '../../ui/IconTextButton';
import { tableBookingApi } from '../tableBookingApi';

const INPUT_BASE_CLASS =
  'w-full rounded-[10px] bg-white/80 border border-gray-200 px-3 py-2 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400';

const TablesSidePanel = ({ onSelectConfiguration, onSelectFloorPlan, onOnboardClick, refreshKey }) => {
  const [sites, setSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [floors, setFloors] = useState([]); // Array of { floor_no, floor_name, section, tableCount }
  const [isLoadingFloors, setIsLoadingFloors] = useState(false);
  const [siteMap, setSiteMap] = useState({}); // Map site_id to site_name
  const [selectedFloorBlock, setSelectedFloorBlock] = useState(null); // Track selected floor-block combination { floor_no, section }
  const [loadingTables, setLoadingTables] = useState(false); // Track loading state for tables

  // Dummy sites data - COMMENTED OUT
  // const dummySites = [
  //   { site_id: '1', site_name: 'Downtown Restaurant' },
  //   { site_id: '2', site_name: 'Mall Location' },
  //   { site_id: '3', site_name: 'Airport Branch' },
  //   { site_id: '4', site_name: 'Beachside Cafe' },
  //   { site_id: '5', site_name: 'City Center' },
  // ];

  // Load sites on mount
  useEffect(() => {
    const loadSites = async () => {
      try {
        setIsLoadingSites(true);
        const response = await getAllSites();
        const sitesData = Array.isArray(response) 
          ? response 
          : (response?.sites || response?.data || []);
        
        // Create site map for quick lookup
        const map = {};
        sitesData.forEach(site => {
          const id = site.site_id || site.id;
          map[id] = site.site_name || site.name || 'Unknown Site';
        });
        setSiteMap(map);
        
        setSites(sitesData);
      } catch (error) {
        console.error('Error loading sites:', error);
        setSites([]);
      } finally {
        setIsLoadingSites(false);
      }
    };
    loadSites();
  }, []);

  // Dummy floor plans data - COMMENTED OUT
  // const dummyFloorPlans = {
  //   '1': [
  //     { floorplan_id: '1-1', site_id: '1', floor_name: 'Ground Floor', block_section: 'Main Dining' },
  //     { floorplan_id: '1-2', site_id: '1', floor_name: 'Ground Floor', block_section: 'Outdoor Patio' },
  //     { floorplan_id: '1-3', site_id: '1', floor_name: 'Second Floor', block_section: 'Private Dining' },
  //     { floorplan_id: '1-4', site_id: '1', floor_name: 'Second Floor', block_section: 'VIP Section' },
  //   ],
  //   // ... more dummy data
  // };

  // Dummy tables data mapped by floorplan_id - COMMENTED OUT
  // const dummyTables = {
  //   '1-1': [
  //     { tableNumber: '01', seater: 2 },
  //     { tableNumber: '02', seater: 4 },
  //     // ... more dummy data
  //   ],
  //   // ... more dummy data
  // };

  // Load floors when site is selected
  useEffect(() => {
    const loadFloors = async () => {
      if (!selectedSiteId) {
        setFloors([]);
        setSelectedFloorBlock(null); // Reset selected floor-block when site changes
        return;
      }

      try {
        setIsLoadingFloors(true);
        setSelectedFloorBlock(null); // Reset selected floor-block when loading new site
        
        // Fetch all tables for the selected site
        const response = await tableBookingApi.getTablesWithFilters({ 
          site_id: selectedSiteId 
        });
        
        const tables = response.tables || [];
        
        // Extract unique floor-block combinations and group by floor_no and section
        const floorBlockMap = new Map();
        
        tables.forEach(table => {
          const floorNo = table.floor_no;
          const section = table.section || ''; // Use empty string if no section
          
          if (floorNo !== null && floorNo !== undefined) {
            // Create a unique key for floor-block combination
            const key = `${floorNo}-${section}`;
            
            if (!floorBlockMap.has(key)) {
              floorBlockMap.set(key, {
                floor_no: floorNo,
                floor_name: table.floor_name || `Floor ${floorNo}`,
                section: section,
                tableCount: 0,
              });
            }
            floorBlockMap.get(key).tableCount += 1;
          }
        });
        
        // Convert map to array and sort by floor_no, then by section
        const floorsArray = Array.from(floorBlockMap.values()).sort((a, b) => {
          if (a.floor_no !== b.floor_no) {
            return a.floor_no - b.floor_no;
          }
          // If same floor, sort by section (empty sections last)
          if (!a.section && b.section) return 1;
          if (a.section && !b.section) return -1;
          return (a.section || '').localeCompare(b.section || '');
        });
        
        setFloors(floorsArray);
      } catch (error) {
        console.error('Error loading floors:', error);
        setFloors([]);
      } finally {
        setIsLoadingFloors(false);
      }
    };

    loadFloors();
  }, [selectedSiteId, refreshKey]); // Reload when refreshKey changes

  // Filter floors based on search query
  const filteredFloors = floors.filter(floor => {
    if (!searchQuery.trim()) return true;
    
    const siteName = siteMap[selectedSiteId] || '';
    const floorName = floor.floor_name || `Floor ${floor.floor_no}`;
    const section = floor.section || '';
    
    const searchLower = searchQuery.toLowerCase();
    return (
      siteName.toLowerCase().includes(searchLower) ||
      floorName.toLowerCase().includes(searchLower) ||
      String(floor.floor_no).includes(searchLower) ||
      section.toLowerCase().includes(searchLower)
    );
  });

  const handleFloorBlockClick = async (floorBlock) => {
    const floorNo = floorBlock.floor_no;
    const section = floorBlock.section || '';
    
    // Create a unique key for this floor-block combination
    const key = `${floorNo}-${section}`;
    
    // Toggle selection
    const currentKey = selectedFloorBlock ? `${selectedFloorBlock.floor_no}-${selectedFloorBlock.section || ''}` : null;
    const newSelectedFloorBlock = currentKey === key ? null : { floor_no: floorNo, section: section };
    setSelectedFloorBlock(newSelectedFloorBlock);
    
    // If deselecting, don't fetch tables
    if (newSelectedFloorBlock === null) {
      return;
    }
    
    // Fetch tables from API for this site, floor, and section
    try {
      setLoadingTables(true);
      
      // Build filters for API call
      const filters = {
        site_id: selectedSiteId,
        floor_no: floorNo,
      };
      
      // Add section filter only if section exists
      if (section) {
        filters.section = section;
      }
      
      // Fetch tables from API
      const response = await tableBookingApi.getTablesWithFilters(filters);
      const apiTables = response.tables || [];
      
      // Transform API table format to component format
      const formattedTables = apiTables.map((table) => ({
        tableNumber: table.table_label || String(table.table_no).padStart(2, '0'),
        seater: table.capacity || 0,
        table_no: table.table_no,
        section: table.section,
        table_label: table.table_label,
        capacity: table.capacity,
      }));
      
      // Pass configuration to parent
      if (onSelectConfiguration) {
        onSelectConfiguration({
          site: selectedSiteId,
          floor: String(floorNo),
          block: section, // Pass the section/block
        });
      }
      
      // Pass floor and tables data to parent
      if (onSelectFloorPlan) {
        onSelectFloorPlan({
          floorPlanId: `${selectedSiteId}-${floorNo}-${section || 'default'}`,
          floorPlan: {
            site_id: selectedSiteId,
            floor_no: floorNo,
            floor_name: floorBlock.floor_name,
            section: section,
          },
          tables: formattedTables,
        });
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      // On error, still pass empty tables
      if (onSelectConfiguration) {
        onSelectConfiguration({
          site: selectedSiteId,
          floor: String(floorNo),
          block: section,
        });
      }
      
      if (onSelectFloorPlan) {
        onSelectFloorPlan({
          floorPlanId: `${selectedSiteId}-${floorNo}-${section || 'default'}`,
          floorPlan: {
            site_id: selectedSiteId,
            floor_no: floorNo,
            floor_name: floorBlock.floor_name,
            section: section,
          },
          tables: [],
        });
      }
    } finally {
      setLoadingTables(false);
    }
  };

  const panelStyle = {
    background:
      'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
    boxShadow:
      '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(30px)',
  };

  return (
    <Card
      className="w-[320px] rounded-xl overflow-hidden flex flex-col h-full"
      style={panelStyle}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/30 bg-white/40 backdrop-blur-sm flex items-center justify-between">
        <h5 className={`${h5} text-gray-900 font-semibold`}>Table</h5>
        <IconTextButton
          icon={<Plus className="w-5 h-5" />}
          text="Onboard tables"
          onClick={() => {
            if (onOnboardClick) {
              onOnboardClick();
            }
          }}
          size="sm"
          variant="default"
        />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Site Selection Dropdown */}
        <div className="px-4 border-b border-white/20 mt-4">
          <label className={`block ${sh2} text-gray-700 mb-2 text-sm`}>Site</label>
          <select
            value={selectedSiteId}
            onChange={(e) =>         setSelectedSiteId(e.target.value)}
            className={INPUT_BASE_CLASS}
          >
            <option value="">
              {isLoadingSites ? 'Loading sites...' : 'Select a site'}
            </option>
            {Array.isArray(sites) && [...sites]
              .sort((a, b) => {
                const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
                const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                return nameA.localeCompare(nameB);
              })
              .map((site) => (
                <option key={site.site_id || site.id} value={site.site_id || site.id}>
                  {site.site_name || site.name || 'Unknown Site'}
                </option>
              ))}
          </select>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-white/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by floor, block, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${INPUT_BASE_CLASS} pl-10`}
              disabled={!selectedSiteId}
            />
          </div>
        </div>

        {/* Floors List */}
        <div className="flex-1 overflow-y-auto p-4">
          {!selectedSiteId ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 text-sm text-center">
                Please select a site to view floors
              </div>
            </div>
          ) : isLoadingFloors ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 text-sm">Loading floors...</div>
            </div>
          ) : filteredFloors.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 text-sm text-center">
                {searchQuery
                  ? 'No floors found matching your search.'
                  : 'No floors found for this site.'}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFloors.map((floorBlock) => {
                const key = `${floorBlock.floor_no}-${floorBlock.section || ''}`;
                const currentKey = selectedFloorBlock ? `${selectedFloorBlock.floor_no}-${selectedFloorBlock.section || ''}` : null;
                const isSelected = currentKey === key;
                const isLoading = loadingTables && isSelected;
                
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => !isLoading && handleFloorBlockClick(floorBlock)}
                    className={`rounded-lg border ${
                      isSelected ? 'border-green-400 bg-green-50/80' : 'border-gray-200 bg-white/80'
                    } shadow-sm p-4 ${isLoading ? 'cursor-wait opacity-75' : 'cursor-pointer hover:bg-white hover:shadow-md'} transition-all`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <h5 className="text-sm font-semibold text-gray-900">
                          {floorBlock.floor_name || `Floor ${floorBlock.floor_no}`}
                        </h5>
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">Floor Number:</span>{' '}
                          {floorBlock.floor_no}
                        </div>
                        {floorBlock.section && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">Block / Section:</span>{' '}
                            {floorBlock.section}
                          </div>
                        )}
                        {!floorBlock.section && (
                          <div className="text-xs text-gray-500 italic">
                            No block/section assigned
                          </div>
                        )}
                        {isLoading && (
                          <div className="text-xs text-blue-600 mt-2">
                            Loading tables...
                          </div>
                        )}
                        {!isLoading && floorBlock.tableCount > 0 && (
                          <div className="text-xs text-gray-500 mt-2">
                            {floorBlock.tableCount} table{floorBlock.tableCount !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TablesSidePanel;
